import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { employerAPI } from '@/utils/api';
import { COUNTRY_CODES, countryName, rwLabel } from '@/constants/remoteWork';
import { TAX_COUNTRIES } from '@/constants/taxIds';

export const FiscalDataCard = ({ user, setUser }) => {
  const { i18n } = useTranslation();
  const L = (k) => rwLabel(i18n.language, k);
  const [country, setCountry] = useState(user?.company_country || 'CV');
  const [nif, setNif] = useState(user?.fiscal_nif || '');
  const [saving, setSaving] = useState(false);
  const isCV = country === 'CV';
  const changed = country !== (user?.company_country || 'CV') || nif.trim() !== (user?.fiscal_nif || '');

  const save = async (e) => {
    e.preventDefault();
    if (user?.nif_verified && !window.confirm('Ao alterar o país ou o número fiscal, a empresa perde o selo "Verificada" até nova verificação pela Trabadju. Continuar?')) return;
    setSaving(true);
    try {
      const { data } = await employerAPI.updateProfile({ company_country: country, fiscal_nif: nif.trim() });
      setUser(data);
      toast.success('Dados fiscais guardados.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 mb-6" data-testid="fiscal-data-card">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="h-4 w-4 text-blue-700" />
        <h3 className="font-semibold text-gray-900">Dados fiscais</h3>
        {user?.nif_verified ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="fiscal-verified-badge"><ShieldCheck className="h-3 w-3" />Verificada</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-testid="fiscal-unverified-badge"><ShieldAlert className="h-3 w-3" />Por verificar</span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">Alterar o país ou o número fiscal remove o selo "Verificada" até nova verificação.</p>
      <form onSubmit={save} className="grid sm:grid-cols-[200px_1fr_auto] gap-2 items-end">
        <label className="text-xs font-medium text-gray-600">{L('country')}
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm" data-testid="fiscal-country-select">
            {(user?.international_request || !TAX_COUNTRIES.includes(user?.company_country || "CV") ? COUNTRY_CODES : TAX_COUNTRIES).map((c) => <option key={c} value={c}>{countryName(c, i18n.language)}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-600">{isCV ? 'NIF (9 dígitos)' : L('foreignNifLabel')}
          <Input value={nif} onChange={(e) => setNif(e.target.value)} maxLength={isCV ? 11 : 40} placeholder={isCV ? '123456789' : L('foreignNifPlaceholder')} className="mt-1" data-testid="fiscal-nif-input" />
        </label>
        <Button type="submit" disabled={saving || !changed || !nif.trim()} data-testid="fiscal-save-btn">{saving ? 'A guardar…' : 'Guardar'}</Button>
      </form>
    </Card>
  );
};
